import { Avatar, Switch } from '@mui/material';
import clsx from 'clsx';
import { memo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SettingsItem } from 'src/components/settings/SettingsItem';
import { getImageURL, getProviderTitleByType } from 'src/helpers';
import { EGetProviderAction, useGetProvidersQuery } from 'src/redux/services/provider';
import { useEditSettingsMutation, useGetSettingsQuery } from 'src/redux/services/settings';
import { ReactComponent as IdIcon } from '../../../icons/Id.svg';
import { CustomTypography } from '../../custom/CustomTypography';
import styles from '../../settings/Settings.module.css';
import { addProvidersTypes, addProviderType } from '../Settings';
import { EditTrustedProviders } from './EditProviders';
import { RootState } from 'src/redux/store';
import { TUserSlice } from 'src/redux/userSlice';
import { connect } from 'react-redux';
import { Roles } from 'src/enums';

const mapStateToProps = ({ user }: RootState) => ({
  roleInApp: user.roleInApp,
});

interface TrustedProvidersComponentProps {
  roleInApp: TUserSlice['roleInApp'];
}

const TrustedProvidersComponent = ({ roleInApp }: TrustedProvidersComponentProps) => {
  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editSettings, { isLoading }] = useEditSettingsMutation();
  const { data: providers = [] } = useGetProvidersQuery({
    client_id: clientId || appId,
    query: {
      action: EGetProviderAction.all,
      types: [
        addProviderType.LDAP,
        addProviderType.ALDPRO,
        addProviderType._1C,
        addProviderType.IDM,
      ].join(','),
    },
  });
  const { data: dataSettings } = useGetSettingsQuery();
  const autoMergeUsers = dataSettings?.auto_merge_users || false; // Значение по умолчанию

  const [localAutoMergeUsers, setLocalAutoMergeUsers] = useState(autoMergeUsers); // Локальное состояние для Switch

  // Синхронизация локального состояния с данными из dataSettings
  useEffect(() => {
    setLocalAutoMergeUsers(autoMergeUsers);
  }, [autoMergeUsers]);

  // Обработчик переключения Switch
  const handleSwitchChange = async (checked: boolean) => {
    try {
      // Отправляем запрос на обновление настроек
      await editSettings({ auto_merge_users: checked }).unwrap();

      // Обновляем локальное состояние только при успешном запросе
      setLocalAutoMergeUsers(checked);
    } catch (error) {
      console.error('Ошибка при обновлении настроек:', error);
      // Здесь можно добавить уведомление об ошибке, если нужно
    }
  };

  return (
    <>
      <SettingsItem title="Доверенные провайдеры" onClick={() => setIsModalOpen(true)}>
        {(roleInApp === Roles.OWNER || roleInApp === Roles.EDITOR) && (
          <div className={styles['settings-item-row']}>
            <div>
              <CustomTypography className={clsx('text-14')}>
                Автоматическое объединение пользователей
              </CustomTypography>
              <CustomTypography className={clsx('text-12')} color="grey">
                Преобразование самозарегистрированных пользователей в доверенные пользователи при
                совпадении email
              </CustomTypography>
              <CustomTypography className={clsx('text-12')} color="grey">
                Работает только при уникальном email
              </CustomTypography>
            </div>
            <Switch
              checked={localAutoMergeUsers}
              onChange={(e) => handleSwitchChange(e.target.checked)}
              disabled={isLoading} // Блокируем переключатель, пока запрос выполняется
            />
          </div>
        )}

        <div className={styles.providers}>
          {providers?.map((provider) => {
            return (
              <div key={provider.id} className={styles.provider}>
                <Avatar
                  src={
                    provider.avatar instanceof File
                      ? URL.createObjectURL(provider.avatar)
                      : provider.avatar?.startsWith('data:')
                      ? provider.avatar
                      : getImageURL(provider.avatar)
                  }
                  className={styles['provider-icon-wrapper']}
                >
                  {!provider.avatar && <IdIcon />}
                </Avatar>
                <div className={styles['provider-name-wrapper']}>
                  <CustomTypography className={clsx('text-14', styles['provider-name'])}>
                    {provider.name}
                  </CustomTypography>
                  <CustomTypography className={clsx('text-12')} color="grey">
                    {getProviderTitleByType(provider.type, addProvidersTypes)}
                  </CustomTypography>
                </div>
              </div>
            );
          })}
        </div>
      </SettingsItem>

      <EditTrustedProviders onClose={() => setIsModalOpen(false)} isOpen={isModalOpen} />
    </>
  );
};

export const TrustedProviders = memo(connect(mapStateToProps)(TrustedProvidersComponent));
